import { i18n } from '@/i18n'
import type { ContentLocale, LocalizedFields, Translated } from '@/types/api'

/** Picks the record name in the active content locale, falling back to EN, then any. */
export function pickName(record: Partial<Translated> | null | undefined, locale: ContentLocale): string {
  if (!record) return ''
  return (
    record[`name_${locale}`] ||
    record.name_en ||
    record.name_ru ||
    record.name_uz ||
    record.name_cyr ||
    ''
  )
}

/** Picks a flat localized label of a joined record (`${prefix}_en` … `${prefix}_cyr`) with pickName's fallbacks. */
export function pickLocalized<Prefix extends string>(
  record: Partial<LocalizedFields<Prefix>> | null | undefined,
  prefix: Prefix,
  locale: ContentLocale,
): string {
  if (!record) return ''
  const value = record as Partial<Record<string, string>>
  return pickName(
    { name_en: value[`${prefix}_en`], name_ru: value[`${prefix}_ru`], name_uz: value[`${prefix}_uz`], name_cyr: value[`${prefix}_cyr`] },
    locale,
  )
}

// Intl formatters are costly to build; keep one per locale and pattern.
const formatters = new Map<string, Intl.DateTimeFormat | Intl.NumberFormat>()

function formatter<T extends Intl.DateTimeFormat | Intl.NumberFormat>(name: string, make: (tag: string) => T): T {
  const key = `${i18n.tag}:${name}`
  let cached = formatters.get(key) as T | undefined
  if (!cached) {
    try {
      cached = make(i18n.tag)
    } catch {
      cached = make('en-GB')
    }
    formatters.set(key, cached)
  }
  return cached
}

/** Every date in the console is numeric ("24.09.2026 20:15"); month names are never spelled out. */
export function formatDateTime(value?: string | null) {
  return formatShortDateTime(value)
}

export function formatDate(value?: string | null) {
  return formatShortDate(value)
}

/** True for an API decimal that is zero in any spelling: "0", "0.000", "-0.00". */
export function isZeroDecimal(value: string) {
  return /^-?0*(?:\.0*)?$/.test(value.trim()) && /\d/.test(value)
}

/**
 * Groups the integer part of an API decimal string without converting it to a
 * binary float, so "12345678.125" never turns into 12345678.124999. Zero is
 * kept: chart axes and other places where "0" itself must show use this.
 */
export function groupDecimal(value: string, fractionDigits?: number) {
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value.trim())
  if (!match) return value
  const [, sign, int, frac = ''] = match
  // A full-width no-break space: groups stay clearly apart ("10 000 000 000") and a large amount
  // never wraps across lines. (The narrow U+202F renders almost invisibly in the interface fonts.)
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0')
  const fraction = fractionDigits === undefined ? frac : frac.padEnd(fractionDigits, '0').slice(0, fractionDigits)
  return `${sign}${grouped}${fraction ? `.${fraction}` : ''}`
}

/**
 * A decimal for display: grouped, and "—" for an empty value or an exact zero
 * (client: zero shows as a dash). The check is on the value itself, so a
 * non-zero value cut to zero by `fractionDigits` ("0.0004", 3) shows "0.000".
 */
export function formatDecimal(value?: string | null, fractionDigits?: number) {
  if (value === null || value === undefined || value === '' || isZeroDecimal(value)) return '—'
  return groupDecimal(value, fractionDigits)
}

/** Rounds an API decimal string half away from zero without a float: ("-12.345", 2) → "-12.35"; a result of zero is unsigned. */
export function roundDecimal(value: string, digits: number) {
  const match = /^(-?)(\d+)(?:\.(\d*))?$/.exec(value.trim())
  if (!match) return value
  const [, sign, int, frac = ''] = match
  const padded = frac.padEnd(digits + 1, '0')
  let units = BigInt(int + padded.slice(0, digits))
  if (Number(padded[digits]) >= 5) units += 1n
  const text = units.toString().padStart(digits + 1, '0')
  const rounded = digits ? `${text.slice(0, -digits)}.${text.slice(-digits)}` : text
  return units === 0n ? rounded : `${sign}${rounded}`
}

/**
 * A value rounded to `digits` for display. Only an exact zero (0, -0, "0.00")
 * or an empty value shows "—"; a non-zero value that rounds to zero shows the
 * rounded zero ("0", "0.000"), never the dash (client 2026-10-24: «Факат мутлак
 * 0 -> "-"»). With `trim` the trailing zeros go, as formatNumber does.
 */
export function formatFixed(value: string | number | null | undefined, digits: number, { trim = false } = {}) {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'number' ? value === 0 : isZeroDecimal(value)) return '—'
  let rounded = typeof value === 'number' ? value.toFixed(digits) : roundDecimal(value, digits)
  // toFixed keeps the sign of a small negative ("-0.00"): a rounded zero is just zero.
  if (rounded.startsWith('-') && isZeroDecimal(rounded)) rounded = rounded.slice(1)
  return trim ? groupDecimal(trimDecimal(rounded)) : groupDecimal(rounded, digits)
}

/** An API decimal without trailing zeros, for editable inputs: "50.0000" → "50". */
export function trimDecimal(value?: string | null) {
  if (!value) return ''
  return value.includes('.') ? value.replace(/\.?0+$/, '') : value
}

/** A decimal as Ucode showed it: grouped thousands and no trailing zeros ("28 000", "5.5"); "—" only for an exact zero. */
export function formatNumber(value?: string | null) {
  if (value === null || value === undefined || value === '') return '—'
  return formatDecimal(trimDecimal(value))
}

const pad = (part: number) => String(part).padStart(2, '0')

/** "24.09.2026 20:15" in the viewer's time zone; one line in every table. */
export function formatShortDateTime(value?: string | null) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** "24.09.2026"; calendar dates (YYYY-MM-DD) are read as written, not shifted by time zone. */
export function formatShortDate(value?: string | null) {
  if (!value) return '—'
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (day) return `${day[3]}.${day[2]}.${day[1]}`
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`
}

export function formatCount(value: number) {
  return formatter('count', (tag) => new Intl.NumberFormat(tag)).format(value)
}

export function initials(name: string) {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('') || '·'
  )
}

export function shortId(id?: string | null) {
  return id ? id.slice(0, 8) : '—'
}

/** Exact sum of decimal strings (BigInt-scaled), for weight totals shown before the server recalculates. */
export function sumDecimals(values: string[]): string {
  const valid = values.map((value) => value.trim()).filter((value) => /^\d+(\.\d+)?$/.test(value))
  if (valid.length === 0) return '0'
  const scale = Math.max(...valid.map((value) => value.split('.')[1]?.length ?? 0))
  const total = valid.reduce((acc, value) => {
    const [int, frac = ''] = value.split('.')
    return acc + BigInt(int + frac.padEnd(scale, '0'))
  }, 0n)
  if (scale === 0) return total.toString()
  const digits = total.toString().padStart(scale + 1, '0')
  return `${digits.slice(0, -scale)}.${digits.slice(-scale)}`
}

const DECIMAL_TEXT = /^\d+(\.\d+)?$/

function scaled(value: string): [bigint, number] {
  const [int, frac = ''] = value.trim().split('.')
  return [BigInt(int + frac), frac.length]
}

function unscaled(value: bigint, scale: number): string {
  if (scale === 0) return value.toString()
  const digits = value.toString().padStart(scale + 1, '0')
  return trimDecimal(`${digits.slice(0, -scale)}.${digits.slice(-scale)}`)
}

/** Exact product of two decimal strings; '' when either is not a decimal. */
export function multiplyDecimals(left?: string, right?: string): string {
  if (!left || !right || !DECIMAL_TEXT.test(left.trim()) || !DECIMAL_TEXT.test(right.trim())) return ''
  const [a, aScale] = scaled(left)
  const [b, bScale] = scaled(right)
  return unscaled(a * b, aScale + bScale)
}

/** Decimal quotient rounded half-up to `places`; '' for missing input or a zero divisor. */
export function divideDecimals(left?: string, right?: string, places = 4): string {
  if (!left || !right || !DECIMAL_TEXT.test(left.trim()) || !DECIMAL_TEXT.test(right.trim())) return ''
  const [a, aScale] = scaled(left)
  const [b, bScale] = scaled(right)
  if (b === 0n) return ''
  const numerator = a * 10n ** BigInt(places + bScale) * 2n
  const denominator = b * 10n ** BigInt(aScale)
  return unscaled((numerator / denominator + 1n) / 2n, places)
}

/**
 * Procurement prices are stored per kg and shown and typed per tonne (owner
 * 2026-10-03: «Цена за 1 т»). Both are exact: the backend keeps 6 decimals
 * per kg, so a price per tonne may have up to 3.
 */
export function pricePerTonne(perKilogram?: string | null): string {
  return trimDecimal(multiplyDecimals(perKilogram ?? '', '1000'))
}

/** The price per kg of a price per tonne (exact; see pricePerTonne). */
export function pricePerKilogram(perTonne?: string | null): string {
  return trimDecimal(divideDecimals(perTonne ?? '', '1000', 9))
}
